import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Check, Eye, AlertCircle, X } from 'lucide-react';
import { useApi } from '../../utils/useApi';
import { WhatsAppBubble, useTemplatePreview } from './WhatsAppPreview';

interface WhatsappTemplate {
  id: string;
  title: string;
  purpose?: string;
  language: string;
  templateName: string;
  content: string;
  parameterCount: number;
  sampleParams: Record<string, string>;
  isActive: boolean;
  createdAt: string;
  category?: string | null;
  pricePerMessage?: string | number | null;
}

interface TemplateSelectorProps {
  selectedTemplate: WhatsappTemplate | null;
  onTemplateSelect: (template: WhatsappTemplate) => void;
  gymId?: string; // used to fetch templates for the current tenant
}

interface PreviewModalProps {
  template: WhatsappTemplate | null;
  isOpen: boolean;
  onClose: () => void;
}

// Preview Modal Component — server-rendered preview substitutes the real gym
// name for gym_data params; user-provided params show as [placeholders] until
// filled in on the next step.
function PreviewModal({ template, isOpen, onClose }: PreviewModalProps) {
  const { previewContent, isLoading } = useTemplatePreview(
    isOpen ? template?.id : undefined,
    {},
    template?.content
  );

  if (!isOpen || !template) return null;

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[1000] p-2 sm:p-4">
      <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-4 max-h-[80vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900">Template Preview</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-full hover:bg-gray-100"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-6">
          <div>
            <h4 className="font-medium text-gray-900 mb-1">{template.title}</h4>
            {template.purpose && (
              <p className="text-sm text-gray-600">{template.purpose}</p>
            )}
          </div>

          <WhatsAppBubble content={previewContent} isLoading={isLoading} />
        </div>

        <div className="flex justify-end mt-6">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200 transition-colors font-medium"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default function TemplateSelector({
  selectedTemplate,
  onTemplateSelect,
  gymId
}: TemplateSelectorProps) {
  const [previewModal, setPreviewModal] = useState<{ isOpen: boolean; template: WhatsappTemplate | null }>({
    isOpen: false,
    template: null
  });

  const { fetchWithAuth } = useApi();

  // Fetch templates
  const { data: templatesData, isLoading, error } = useQuery({
    queryKey: ['enhanced-templates', gymId],
    queryFn: async () => {
      return await fetchWithAuth(`/api/enhanced-templates?gymId=${gymId}`);
    },
    enabled: !!gymId
  });

  const templates: WhatsappTemplate[] = templatesData?.data || [];

  const handleTemplateSelect = (template: WhatsappTemplate) => {
    onTemplateSelect(template);
  };

  const handlePreview = (template: WhatsappTemplate) => {
    setPreviewModal({ isOpen: true, template });
  };

  const closePreviewModal = () => {
    setPreviewModal({ isOpen: false, template: null });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600">Loading templates...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-center">
          <AlertCircle className="h-5 w-5 text-red-400 mr-2" />
          <span className="text-red-800">Failed to load templates. Please try again.</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Templates List */}
      <div className="space-y-3">
        {templates.map((template) => (
          <div
            key={template.id}
            className={`group flex items-center justify-between p-5 border rounded-xl transition-all duration-300 cursor-pointer ${selectedTemplate?.id === template.id
                ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200 shadow-lg'
                : 'border-gray-200 hover:border-gray-300 hover:shadow-md hover:bg-gray-50'
              }`}
            onClick={() => handleTemplateSelect(template)}
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2 min-w-0">
                  <h3 className="font-semibold text-gray-900 truncate group-hover:text-blue-600 transition-colors">
                    {template.title}
                  </h3>
                  {template.category && (
                    <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-blue-100 text-blue-700 flex-shrink-0">
                      {template.category}
                    </span>
                  )}
                  {template.pricePerMessage != null && Number(template.pricePerMessage) > 0 && (
                    <span className="text-[10px] text-gray-500 flex-shrink-0">₹{Number(template.pricePerMessage).toFixed(2)}/msg</span>
                  )}
                </div>
                {selectedTemplate?.id === template.id && (
                  <Check className="h-5 w-5 text-blue-600 ml-3 flex-shrink-0" />
                )}
              </div>

              {template.purpose && (
                <p className="text-sm text-gray-600 mb-3 line-clamp-1">{template.purpose}</p>
              )}

              <div className="text-sm text-gray-700 line-clamp-2 leading-relaxed">
                {template.content}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-3 ml-6 flex-shrink-0">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handlePreview(template);
                }}
                className="p-2.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all duration-200 group-hover:bg-blue-50 group-hover:text-blue-600"
                title="Preview template"
              >
                <Eye className="h-4 w-4" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleTemplateSelect(template);
                }}
                className={`px-5 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 ${selectedTemplate?.id === template.id
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'bg-blue-100 text-blue-700 hover:bg-blue-200 group-hover:bg-blue-600 group-hover:text-white'
                  }`}
              >
                {selectedTemplate?.id === template.id ? 'Selected' : 'Select'}
              </button>
            </div>
          </div>
        ))}
      </div>

      {templates.length === 0 && (
        <div className="text-center py-8">
          <div className="text-gray-500 mb-2">No templates available</div>
          <div className="text-sm text-gray-400">
            Contact your administrator to add notification templates
          </div>
        </div>
      )}

      {/* Preview Modal */}
      <PreviewModal
        template={previewModal.template}
        isOpen={previewModal.isOpen}
        onClose={closePreviewModal}
      />
    </div>
  );
}
